import { smoothPenSample } from './smoothPenSample.js'
import { shouldShieldKeyboardEvent, isEditableTarget } from '../../../commands/commandModel.js'
import { DRAWING_CURSORS } from './drawingCursor.js'
import { createDrawingProjection } from './freehandGeometry.js'

// Native capture listeners claim only an explicit drawing gesture. Ordinary
// pointer navigation does no hit-testing and never changes chart options.
export function installFreehandInteraction({ api, overlay, timeframe, getState, onSelect, onSettings, suppressClicks }) {
  const pane = api.chart.panes?.()[api.paneIndex ?? 0], element = pane?.getHTMLElement()
  if (!element) return () => {}
  let gesture = null, frame = 0, shiftHeld = false
  const position = e => { const box=element.getBoundingClientRect(); return {x:e.clientX-box.left-api.chart.priceScale('left').width(),y:e.clientY-box.top} }
  const visible = state => state.items.filter(item=>item.visible!==false && (!item.timeframes?.length || item.timeframes.includes(timeframe)))
  const redraw = () => {
    frame=0
    const state=getState()
    overlay.selectionBox=gesture?.kind==='box' ? [gesture.start,gesture.last] : null
    overlay.update([...visible(state).filter(item=>!gesture?.erased.has(item.id)), ...(gesture?.stroke?[gesture.stroke]:[])], state.selected)
  }
  const schedule = () => { if(!frame)frame=requestAnimationFrame(redraw) }
  const stop = event => { event?.preventDefault(); event?.stopImmediatePropagation() }
  const erase = point => {
    let id
    while ((id=overlay.pick(point.x,point.y))) {
      gesture.erased.add(id)
      overlay.items=overlay.items.filter(item=>item.id!==id)
    }
  }
  const finish = (save, event) => {
    if(!gesture)return
    const previous=gesture;gesture=null
    if(previous.polyline)previous.stroke.points=previous.stroke.points.slice(0,previous.fixedCount)
    if(frame)cancelAnimationFrame(frame);frame=0
    try {api.chart.applyOptions(previous.options)} catch { /* chart disposed */ }
    if(element.hasPointerCapture?.(previous.id))element.releasePointerCapture(previous.id)
    element.style.cursor=previous.cursor
    const state=getState()
    if(save) {
      if(previous.stroke && (!previous.polyline || previous.stroke.points.length>1))state.commit([...state.items,previous.stroke])
      if(previous.erased.size)state.commit(state.items.filter(item=>!previous.erased.has(item.id)))
      if(previous.kind==='box'){state.onToolChange('select');onSelect(overlay.selectBox(previous.start,previous.last))}
    }
    redraw();suppressClicks();stop(event)
  }
  const beginStraight = () => {
    if (!gesture?.stroke || gesture.straight != null) return
    const points = gesture.stroke.points
    points[points.length - 1] = { ...points.at(-1), corner: true }
    gesture.straight = points.length
  }
  const addPenPoint = (p, straight) => {
    const points = gesture.stroke.points, anchor = gesture.projection.point(p)
    if (!anchor) return
    if (straight) beginStraight()
    else gesture.straight = null
    if (gesture.straight != null) {
      if (gesture.straight < 2048) points[gesture.straight] = { ...anchor, corner: true }
    } else if (p.x !== gesture.last.x || p.y !== gesture.last.y) {
      if (points.length >= 2048) {
        // Retain straight-section joins when thinning long freehand gestures.
        gesture.stroke.points = points.filter((point,i,rows)=>point.corner || i%2===0 || i===rows.length-1)
      }
      const samples = gesture.stroke.points
      if (samples.length < 2048) {
        if (gesture.beforePreviousSample && !samples.at(-1).corner) {
          const smoothed = smoothPenSample(gesture.beforePreviousSample, gesture.previousSample, p, getState().penStyle.smoothing)
          const previousAnchor = gesture.projection.point(smoothed)
          if (previousAnchor) samples[samples.length - 1] = previousAnchor
        }
        samples.push(anchor)
        gesture.beforePreviousSample = gesture.previousSample
        gesture.previousSample = p
      }
    } else return
    gesture.last = p
  }
  const down = e => {
    const state=getState()
    if(e.button!==0||!state.ready||element.closest('[inert]'))return
    const p=position(e)
    if(p.x<0||p.x>api.chart.timeScale().width()||p.y<0||p.y>pane.getHeight())return
    if (gesture?.polyline) {
      gesture.id=e.pointerId;gesture.pressed=true
      const points=gesture.stroke.points.slice(0,gesture.fixedCount)
      const first=gesture.projection.project(points[0]), last=gesture.projection.project(points.at(-1))
      if (gesture.fixedCount>=3 && Math.hypot(p.x-first.x,p.y-first.y)<=7) {
        gesture.stroke.points=[...points,{...points[0]}];gesture.fixedCount++
        finish(true,e);return
      }
      const anchor=gesture.projection.point(p)
      if(anchor && Math.hypot(p.x-last.x,p.y-last.y)>2 && points.length<2047) {
        gesture.stroke.points=[...points,{...anchor,corner:true}];gesture.fixedCount++
      }
      gesture.last=p;schedule();suppressClicks();stop(e);return
    }
    if(e.metaKey && !e.shiftKey) {
      const id=overlay.pick(p.x,p.y)
      if(id){const ids=Array.isArray(state.selected)&&state.selected.includes(id)?state.selected:[id];state.onToolChange('select');onSelect(ids);onSettings(ids);state.onActivate();suppressClicks();stop(e)}
      return
    }
    const kind=e.metaKey && e.shiftKey ? 'box' : state.tool
    if(!['pen','eraser','box'].includes(kind))return
    if(kind==='pen'&&state.items.length>=200){state.onLimit?.();return}
    const projection=createDrawingProjection(api,timeframe), anchor=projection.point(p)
    if(kind==='pen'&&!anchor)return
    gesture={kind,id:e.pointerId,start:p,last:p,previousSample:p,beforePreviousSample:null,straight:null,pressed:true,startShift:Boolean(e.shiftKey||shiftHeld),travel:0,projection,erased:new Set(),cursor:element.style.cursor,
      options:structuredClone({handleScroll:api.chart.options().handleScroll,handleScale:api.chart.options().handleScale}),
      stroke:kind==='pen'?{_draft:true,id:crypto.randomUUID(),kind:'pen',points:[anchor],text:'',color:state.penStyle.color,width:state.penStyle.width,timeframes:[timeframe]}:null}
    api.chart.applyOptions({handleScroll:false,handleScale:false})
    element.style.cursor=DRAWING_CURSORS[kind] || 'crosshair'
    if(kind==='pen' && (e.shiftKey || shiftHeld))beginStraight()
    state.onActivate();element.setPointerCapture?.(e.pointerId)
    if(kind==='eraser')erase(p)
    schedule();suppressClicks();stop(e)
  }
  const move = e => {
    if(!gesture||e.pointerId!==gesture.id)return
    const raw=position(e)
    gesture.travel=Math.max(gesture.travel,Math.hypot(raw.x-gesture.start.x,raw.y-gesture.start.y))
    if(gesture.polyline) {
      const anchor=gesture.projection.point(raw)
      if(anchor)gesture.stroke.points=[...gesture.stroke.points.slice(0,gesture.fixedCount),{...anchor,corner:true}]
      gesture.last=raw;schedule();stop(e);return
    }
    const p=raw
    const distance=Math.hypot(p.x-gesture.last.x,p.y-gesture.last.y)
    if(gesture.kind==='pen') { addPenPoint(p, e.shiftKey || shiftHeld) } else if(gesture.kind==='eraser') {
      const steps=Math.max(1,Math.ceil(distance/4))
      for(let i=1;i<=steps;i++)erase({x:gesture.last.x+(p.x-gesture.last.x)*i/steps,y:gesture.last.y+(p.y-gesture.last.y)*i/steps})
      gesture.last=p
    } else if(gesture.kind==='box')gesture.last=p
    schedule();stop(e)
  }
  const up=e=>{
    if(gesture?.id!==e.pointerId)return
    if(gesture.polyline) {gesture.pressed=false;suppressClicks();stop(e);return}
    move(e)
    if(gesture.kind==='pen' && gesture.startShift && gesture.travel<3) {
      gesture.polyline=true;gesture.pressed=false;gesture.fixedCount=1
      gesture.stroke.points=[{...gesture.stroke.points[0],corner:true}]
      if(element.hasPointerCapture?.(gesture.id))element.releasePointerCapture(gesture.id)
      schedule();suppressClicks();stop(e);return
    }
    finish(true,e)
  }
  const cancel=e=>{if(gesture?.id===e.pointerId && !(e.type==='lostpointercapture' && gesture.polyline && !gesture.pressed))finish(false,e)}
  const blur=()=>{shiftHeld=false;finish(false)}
  const key=e=>{
    if (e.defaultPrevented || e.isComposing || isEditableTarget(e.target) || isEditableTarget(document.activeElement)) return
    // Modifier keys are intentionally shielded by the command router, but Shift
    // belongs to the active drawing gesture. Let keyup reach every chart pane.
    if(e.key==='Shift') {
      shiftHeld=e.type==='keydown'
      if (!shiftHeld && gesture?.kind === 'pen') { finish(true); return }
      if(shiftHeld && gesture?.kind==='pen' && !gesture.polyline) { beginStraight(); schedule() }
      return
    }
    if(shouldShieldKeyboardEvent(e))return
    if(e.key==='Escape')finish(false,e)
    if(e.key==='Enter' && e.type==='keydown' && gesture?.polyline)finish(true,e)
  }
  const doubleClick=e=>{if(gesture?.polyline)finish(true,e)}
  const outside=e=>{if(gesture?.polyline && !element.contains(e.target))finish(false)}
  element.addEventListener('dblclick',doubleClick,true);document.addEventListener('pointerdown',outside,true)
  for(const [type,fn] of [['pointerdown',down],['pointermove',move],['pointerup',up],['pointercancel',cancel],['lostpointercapture',cancel]])element.addEventListener(type,fn,true)
  window.addEventListener('blur',blur);window.addEventListener('keydown',key,true);window.addEventListener('keyup',key,true)
  const dispose = ()=>{finish(false);element.removeEventListener('dblclick',doubleClick,true);document.removeEventListener('pointerdown',outside,true);for(const [type,fn] of [['pointerdown',down],['pointermove',move],['pointerup',up],['pointercancel',cancel],['lostpointercapture',cancel]])element.removeEventListener(type,fn,true);window.removeEventListener('blur',blur);window.removeEventListener('keydown',key,true);window.removeEventListener('keyup',key,true)}
  dispose.cancel = () => finish(false)
  return dispose
}
